// ============================================================================
// REFERRAL PINGS VIEW — internal attention system (referral-bound, non-PHI)
//
// Lists referral pings for the current user. Pings live under
// tenants/{tenantId}/referrals/{referralId}/communications/{communicationId}
// (the referral is the thread). This page never shows PHI and never moves
// documents — it only surfaces "what action is needed on which referral".
//
// Tabs:
//   Waiting on Me — recipientUid == me AND status in OPEN/ACKNOWLEDGED
//   Sent by Me    — senderUid == me
//   Resolved      — status == RESOLVED (mine, sent or received)
//   Cancelled     — status == CANCELLED (mine, sent or received)
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  Bell, RefreshCw, AlertTriangle, Loader2, ArrowRight, Check, Ban,
  Inbox, Send, CheckCircle2, XCircle, Clock, ChevronDown, MessageSquare,
} from 'lucide-react';
import {
  acknowledgeCommunicationPing,
  cancelCommunicationPing,
  resolveCommunicationPing,
  getTenantUser,
  getWorkflowState,
  postCommunicationReply,
  subscribeToCommunicationMessages,
  subscribeToPingsForRecipient,
  subscribeToPingsForSender,
  markPingRead,
  isPingUnread,
  MESSAGE_REPLY_MAX_LENGTH,
  MESSAGE_TYPE,
  COMMUNICATION_STATUS,
  COMMUNICATION_PURPOSE_LABELS,
} from '../core/index.js';
import { ROLE_DISPLAY } from '../core/rbac.js';
import QueuePingsPanel from './QueuePingsPanel.jsx';

// Where "Open Referral" lands per role (best-effort jump to the referral surface).
const OPEN_REFERRAL_ROUTE = {
  gp_employee:        'not-yet-inbox',
  intake_coordinator: 'workqueue',
  triage_nurse:       'triage-queue',
  scheduler:          'scheduling',
  specialist:         'specialist-worklist',
  specialist_admin:   'specialist-admin-workplace',
  tenant_admin:       'audit-logs',
};

const STATUS_STYLE = {
  OPEN:         'bg-amber-500/15 text-amber-300 border-amber-500/25',
  ACKNOWLEDGED: 'bg-sky-500/15 text-sky-300 border-sky-500/25',
  RESOLVED:     'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
  CANCELLED:    'bg-slate-600/30 text-slate-400 border-slate-600/30',
};

const TABS = [
  { id: 'waiting',   label: 'Waiting on Me', icon: Inbox },
  { id: 'sent',      label: 'Sent by Me',    icon: Send },
  { id: 'resolved',  label: 'Resolved',      icon: CheckCircle2 },
  { id: 'cancelled', label: 'Cancelled',     icon: XCircle },
];

function fmtWhen(ts) {
  if (!ts) return '—';
  const ms = ts.toMillis ? ts.toMillis() : (ts.seconds ? ts.seconds * 1000 : (ts instanceof Date ? ts.getTime() : Date.parse(ts)));
  if (!ms || Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function roleLabel(role) {
  return ROLE_DISPLAY[role]?.name || role || '—';
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLE[status] || STATUS_STYLE.CANCELLED}`}>
      {status}
    </span>
  );
}

function avatarInitial(label) {
  const s = (label || '').trim();
  return s ? s[0].toUpperCase() : '•';
}

// Right-panel "Ping Thread": reads like a referral-bound conversation, not a
// task record. The ping itself is the opening message; status changes render as
// smaller system rows. Metadata is demoted into a collapsible footer.
// Pure presentational — no PHI, no documents, no reply storage (v1).
function PingThreadDetail({
  selected, resolveName, canAck, canCancel, canMarkDone, canResolve, resolveLabel = 'Resolve Ping', actionBusy, actionError, onOpenReferral, onAck, onCancel, onMarkDone, onResolve,
  currentUid, messages = [], newMsgIds, messagesLoading, messagesError, onRetryMessages,
  canReply, replyBody, setReplyBody, replySending, replyError, onSendReply, activeFocusTarget,
}) {
  const hl = (target) => (activeFocusTarget === target ? 'learning-focus-highlight' : '');
  const title = COMMUNICATION_PURPOSE_LABELS[selected.purpose] || selected.purpose;
  const senderName    = resolveName(selected.senderUid, selected.senderRole);
  const recipientName = resolveName(selected.recipientUid, selected.recipientRole);
  const senderRoleText = roleLabel(selected.senderRole);

  const systemEvents = [];
  if (selected.acknowledgedAt) systemEvents.push({ key: 'ack', label: 'Ping acknowledged', at: selected.acknowledgedAt, Icon: Check,        dot: 'bg-sky-400' });
  if (selected.resolvedAt)     systemEvents.push({ key: 'res', label: 'Ping marked done.',  at: selected.resolvedAt,     Icon: CheckCircle2, dot: 'bg-emerald-400' });
  if (selected.cancelledAt)    systemEvents.push({ key: 'can', label: 'Ping cancelled',    at: selected.cancelledAt,    Icon: Ban,          dot: 'bg-slate-500' });

  // Merge user replies + system status events into one chronological timeline
  // (oldest first) that renders below the opening ping message.
  const timeline = [
    ...messages.map((m) => ({ kind: 'reply', key: `r_${m.id}`, at: m.createdAt, msg: m })),
    ...systemEvents.map((e) => ({ kind: 'system', key: `s_${e.key}`, at: e.at, ev: e })),
  ].sort((a, b) => fmtMs(a.at) - fmtMs(b.at));

  const replyLen = (replyBody || '').length;
  const overLimit = replyLen > MESSAGE_REPLY_MAX_LENGTH;

  return (
    <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 overflow-hidden">
      {/* Header — action/purpose is the main title; referral ID is the anchor below it */}
      <div className="px-6 pt-5 pb-4 border-b border-slate-800/60">
        <p className="text-[10px] uppercase tracking-wider text-violet-400/80 font-semibold mb-1.5">Ping Thread</p>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-white break-normal">{title}</h2>
            <p className="text-xs text-slate-500 font-mono mt-0.5 truncate">Referral {selected.referralId}</p>
          </div>
          <StatusBadge status={selected.status} />
        </div>
      </div>

      {/* Action needed */}
      <div className="px-6 py-4 border-b border-slate-800/60 bg-slate-800/20">
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Action needed</p>
        <p className="text-sm text-slate-200 leading-relaxed">{selected.actionNeeded}</p>
      </div>

      {/* Thread timeline */}
      <div className={`px-6 py-5 space-y-4 ${hl('referral_pings_thread_area')}`}>
        {/* Opening message — the sender's optional note only. The canned action
            text lives in Action Needed above and is never duplicated as a message. */}
        {selected.message ? (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-violet-300">
              {avatarInitial(senderName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-200 truncate">{senderName}</p>
              <p className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                {senderRoleText}
                <span className="text-slate-700">·</span>
                <Clock className="w-3 h-3" />{fmtWhen(selected.createdAt)}
              </p>
              <div className="mt-1.5 rounded-2xl rounded-tl-sm bg-slate-800/70 border border-slate-700/40 px-3.5 py-2.5">
                <p className="text-sm text-slate-200 leading-relaxed">{selected.message}</p>
              </div>
            </div>
          </div>
        ) : (
          // No optional note → no empty bubble; a small system row anchors the start.
          <div className="flex items-center gap-2 pl-2 text-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
            <span className="text-slate-400">Ping sent</span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-500">{fmtWhen(selected.createdAt)}</span>
          </div>
        )}

        {/* Merged timeline: replies + system status events, chronological */}
        {timeline.map((item) => {
          if (item.kind === 'system') {
            const ev = item.ev;
            return (
              <div key={item.key} className="flex items-center gap-2 pl-2 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full ${ev.dot} flex-shrink-0`} />
                <ev.Icon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                <span className="text-slate-400">{ev.label}</span>
                <span className="text-slate-600">·</span>
                <span className="text-slate-500">{fmtWhen(ev.at)}</span>
              </div>
            );
          }
          const m = item.msg;
          // System movement message — distinct row, not a chat bubble.
          if (m.messageType === MESSAGE_TYPE.SYSTEM_EVENT) {
            const isNewSys = newMsgIds?.has?.(m.id);
            return (
              <div key={item.key} className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5 bg-emerald-500/10 border border-emerald-500/20 ${isNewSys ? 'ring-2 ring-emerald-400/40' : ''}`}>
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-400/80 font-semibold">System update</p>
                  <p className="text-sm text-slate-200 leading-relaxed mt-0.5">{m.body}</p>
                  <p className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />{fmtWhen(m.createdAt)}
                  </p>
                </div>
              </div>
            );
          }
          const authorName = resolveName(m.authorUid, m.authorRole);
          const mine = m.authorUid === currentUid;
          const isNew = newMsgIds?.has?.(m.id);
          return (
            <div key={item.key} className={`flex items-start gap-3 ${mine ? 'flex-row-reverse' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold border ${mine ? 'bg-sky-500/20 border-sky-500/30 text-sky-300' : 'bg-slate-700/40 border-slate-600/40 text-slate-300'}`}>
                {avatarInitial(authorName)}
              </div>
              <div className={`min-w-0 flex-1 ${mine ? 'flex flex-col items-end' : ''}`}>
                <p className="text-[11px] text-slate-500">
                  <span className="text-slate-300 font-medium">{authorName}</span>
                  <span className="text-slate-700"> · </span>{roleLabel(m.authorRole)}
                  <span className="text-slate-700"> · </span>{fmtWhen(m.createdAt)}
                  {isNew && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">New</span>}
                </p>
                <div className={`mt-1 inline-block rounded-2xl px-3.5 py-2 max-w-full transition-all ${mine ? 'rounded-tr-sm bg-sky-600/20 border border-sky-500/25' : 'rounded-tl-sm bg-slate-800/70 border border-slate-700/40'} ${isNew ? 'ring-2 ring-emerald-400/40' : ''}`}>
                  <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap break-words">{m.body}</p>
                </div>
              </div>
            </div>
          );
        })}

        {/* Thread load states */}
        {messagesLoading && (
          <div className="flex items-center gap-2 text-xs text-slate-500 pl-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading replies…
          </div>
        )}
        {messagesError && (
          <div className="flex items-center gap-2 text-xs text-red-300 pl-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{messagesError}</span>
            <button onClick={onRetryMessages} className="underline underline-offset-2 hover:text-red-200">Retry</button>
          </div>
        )}
      </div>

      {/* Reply composer — sender/recipient only (tenant_admin reads, no composer) */}
      {canReply && (
        <div className="px-6 pb-4 border-t border-slate-800/60 pt-4">
          <textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            rows={2}
            maxLength={MESSAGE_REPLY_MAX_LENGTH + 50 /* soft cap; hard-blocked on send */}
            placeholder="Reply without PHI or documents…"
            className="w-full rounded-xl bg-slate-800/60 border border-slate-700/50 px-3.5 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none"
          />
          <div className="flex items-center justify-between gap-3 mt-2">
            <span className={`text-[11px] ${overLimit ? 'text-red-400' : 'text-slate-500'}`}>
              {replyLen}/{MESSAGE_REPLY_MAX_LENGTH}
            </span>
            <button
              onClick={onSendReply}
              disabled={replySending || replyBody.trim() === '' || overLimit}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-medium transition-all"
            >
              {replySending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Reply
            </button>
          </div>
          {replyError && (
            <div className="flex items-start gap-2 mt-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-300">{replyError}</p>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {actionError && (
        <div className="mx-6 mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-300">{actionError}</p>
        </div>
      )}

      {/* Actions */}
      <div className={`px-6 pb-5 flex flex-wrap gap-2 rounded-xl ${hl('referral_pings_action_row')}`}>
        <button
          onClick={onOpenReferral}
          disabled={!selected.referralId}
          title={!selected.referralId ? 'This ping is missing a referral link.' : undefined}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-all ${hl('referral_pings_open_referral')}`}
        >
          <ArrowRight className="w-4 h-4" /> Open Referral
        </button>
        {canAck && (
          <button
            onClick={onAck}
            disabled={actionBusy}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-sm font-medium transition-all"
          >
            {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Acknowledge
          </button>
        )}
        {canMarkDone && (
          <button
            onClick={onMarkDone}
            disabled={actionBusy}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium transition-all"
          >
            {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Mark Done
          </button>
        )}
        {canResolve && (
          <button
            onClick={onResolve}
            disabled={actionBusy}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium transition-all"
          >
            {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {resolveLabel}
          </button>
        )}
        {canCancel && (
          <button
            onClick={onCancel}
            disabled={actionBusy}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-600 disabled:opacity-50 text-sm font-medium transition-all"
          >
            {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Cancel
          </button>
        )}
      </div>

      {/* Referral details — de-emphasized, collapsible footer */}
      <details className="group border-t border-slate-800/60">
        <summary className="cursor-pointer list-none px-6 py-3 flex items-center justify-between text-xs font-medium text-slate-400 hover:text-slate-200 transition-colors">
          <span className="inline-flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Referral details</span>
          <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
        </summary>
        <div className="px-6 pb-4 grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">From</p>
            <p className="text-slate-200 truncate">{senderName}</p>
            <p className="text-[11px] text-slate-500">{senderRoleText}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">To</p>
            <p className="text-slate-200 truncate">{recipientName}</p>
            <p className="text-[11px] text-slate-500">{roleLabel(selected.recipientRole)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Referral</p>
            <p className="text-slate-300 font-mono text-xs break-all">{selected.referralId}</p>
          </div>
          {selected.relatedObjectType && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Linked</p>
              <p className="text-slate-300 text-xs">{selected.relatedObjectType}{selected.relatedObjectId ? ` · ${selected.relatedObjectId}` : ''}</p>
            </div>
          )}
        </div>
      </details>
    </div>
  );
}

export default function ReferralPingsView({ onNavigate, activeFocusTarget }) {
  const { user } = useAuth();
  const uid = user?.uid;
  const tenantId = user?.tenantId;
  // Learning-module focus highlight (UNDERSTAND REFERRAL PINGS module).
  const hl = (target) => (activeFocusTarget === target ? 'learning-focus-highlight' : '');
  // Glow-only variant (no scale/bounce) — used for large containers like the header.
  const hlSubtle = (target) => (activeFocusTarget === target ? 'learning-focus-highlight-subtle' : '');

  const [tab, setTab] = useState('waiting');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'referral' | 'queue'
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  // uid → user profile ({ name, email, ... }) resolved from users/{uid} (tenant-scoped).
  // Render-time resolution only — names are NOT written into communication docs.
  const [profiles, setProfiles] = useState({});

  // Reply thread (messages subcollection of the selected ping).
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState(null);
  // workflow/state for the selected ping's referral — gates Mark Done (Not-Yet recovery).
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  // Live UI feedback.
  const [toast, setToast] = useState(null);
  const [newMsgIds, setNewMsgIds] = useState(() => new Set()); // briefly-highlighted incoming replies
  const [refreshKey, setRefreshKey] = useState(0);             // manual re-subscribe (Refresh button)
  const [msgRefreshKey, setMsgRefreshKey] = useState(0);       // manual thread re-subscribe (Retry)

  const toastTimerRef   = useRef(null);
  const newIdsTimerRef  = useRef(null);
  const detailRef       = useRef(null); // Ping Thread panel — scroll into view on select
  const seenMsgIdsRef   = useRef(new Set()); // message ids already known for the current thread
  const threadInitedRef = useRef(false);     // first snapshot of the current thread = initial load

  const showToast = useCallback((text) => {
    setToast(text);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2500);
  }, []);

  // Live ping list — two collection-group listeners (recipient + sender).
  useEffect(() => {
    if (!tenantId || !uid) { setError('No tenant context.'); setLoading(false); return; }
    setError(null);
    setLoading(true);
    let gotR = false, gotS = false;
    const settle = () => { if (gotR && gotS) { setLoading(false); setRefreshing(false); } };
    const onErr = (err) => {
      console.error('[ReferralPingsView] live ping list failed:', err);
      setError('Could not load referral pings. Please retry.');
      setLoading(false);
      setRefreshing(false);
    };
    const unsubR = subscribeToPingsForRecipient(tenantId, uid, {
      onData: (rows) => { setReceived(rows); gotR = true; settle(); },
      onError: onErr,
    });
    const unsubS = subscribeToPingsForSender(tenantId, uid, {
      onData: (rows) => { setSent(rows); gotS = true; settle(); },
      onError: onErr,
    });
    return () => { unsubR(); unsubS(); };
  }, [tenantId, uid, refreshKey]);

  // Clear timers on unmount.
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    if (newIdsTimerRef.current) clearTimeout(newIdsTimerRef.current);
  }, []);

  // Resolve sender/recipient display names from users/{uid} — batched + deduped.
  // Only fetch uids we have not already resolved; failures fall back silently.
  useEffect(() => {
    if (!tenantId) return;
    const uids = new Set();
    for (const p of [...received, ...sent]) {
      if (p.senderUid) uids.add(p.senderUid);
      if (p.recipientUid) uids.add(p.recipientUid);
    }
    const missing = [...uids].filter((id) => !(id in profiles));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        missing.map(async (id) => {
          try { return [id, await getTenantUser(tenantId, id)]; }
          catch { return [id, null]; }
        }),
      );
      if (cancelled) return;
      setProfiles((prev) => {
        const next = { ...prev };
        for (const [id, profile] of results) next[id] = profile || null;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [received, sent, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Display name for a uid: profile name → profile email (staff email is shown
  // elsewhere in the app and is non-PHI) → role label. Never a patient identifier.
  const resolveName = useCallback((uid, roleFallback) => {
    const profile = uid ? profiles[uid] : null;
    return profile?.name || profile?.email || roleLabel(roleFallback);
  }, [profiles]);

  // Live reply thread for the selected ping. New incoming replies (authored by
  // someone else, after the initial load) raise a "New reply received." toast and
  // briefly highlight. Re-subscribes on ping change or manual Retry.
  const selectedId = selected?.id;
  const selTenantId = selected?.tenantId;
  const selReferralId = selected?.referralId;
  useEffect(() => {
    // Reset per-thread state on (re)subscribe.
    setReplyBody('');
    setReplyError(null);
    setMessages([]);
    setMessagesError(null);
    setNewMsgIds(new Set());
    seenMsgIdsRef.current = new Set();
    threadInitedRef.current = false;
    if (!selectedId || !selTenantId || !selReferralId) return;

    setMessagesLoading(true);
    const unsub = subscribeToCommunicationMessages(selTenantId, selReferralId, selectedId, {
      onData: (rows) => {
        setMessagesLoading(false);
        setMessagesError(null);
        if (!threadInitedRef.current) {
          // Initial load — record ids, never toast for pre-existing messages.
          threadInitedRef.current = true;
          seenMsgIdsRef.current = new Set(rows.map((r) => r.id));
          setMessages(rows);
          return;
        }
        const incoming = rows.filter((r) => !seenMsgIdsRef.current.has(r.id));
        const userIncoming   = incoming.filter((r) => r.messageType !== MESSAGE_TYPE.SYSTEM_EVENT && r.authorUid && r.authorUid !== uid);
        const systemIncoming = incoming.filter((r) => r.messageType === MESSAGE_TYPE.SYSTEM_EVENT);
        seenMsgIdsRef.current = new Set(rows.map((r) => r.id));
        setMessages(rows);
        const highlight = [...userIncoming, ...systemIncoming];
        if (highlight.length > 0) {
          showToast(userIncoming.length > 0 ? 'New reply received.' : 'Referral updated.');
          setNewMsgIds(new Set(highlight.map((r) => r.id)));
          if (newIdsTimerRef.current) clearTimeout(newIdsTimerRef.current);
          newIdsTimerRef.current = setTimeout(() => setNewMsgIds(new Set()), 4000);
        }
      },
      onError: (err) => {
        console.error('[ReferralPingsView] live thread failed:', err);
        setMessagesLoading(false);
        setMessagesError('Could not load live replies.');
      },
    });
    return () => { unsub(); };
  }, [selectedId, selTenantId, selReferralId, msgRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a ping is selected, bring the Ping Thread panel into view and center it
  // (matters on narrow screens where the detail sits below the list).
  useEffect(() => {
    if (!selectedId || !detailRef.current) return;
    const t = setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => clearTimeout(t);
  }, [selectedId]);

  // Load workflow/state for the selected ping's referral to gate Mark Done.
  // Re-fetches when the thread grows (a live system event implies recovery moved).
  const msgCount = messages.length;
  useEffect(() => {
    setSelectedWorkflow(null);
    if (!selectedId || !selTenantId || !selReferralId) return;
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowState(selTenantId, selReferralId);
        if (!cancelled) setSelectedWorkflow(wf || null);
      } catch {
        if (!cancelled) setSelectedWorkflow(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, selTenantId, selReferralId, msgCount]);

  // Composer only for active threads; sender or recipient.
  const isActiveStatus = selected &&
    (selected.status === COMMUNICATION_STATUS.OPEN || selected.status === COMMUNICATION_STATUS.ACKNOWLEDGED);
  const canReply = !!selected && isActiveStatus && (selected.senderUid === uid || selected.recipientUid === uid);

  // Mark Done gating — sender or tenant_admin, on an active ping, with a reliable
  // non-PHI completion signal on the referral's workflow/state.
  const isTenantAdminUser = user?.role === 'tenant_admin';
  const isSenderOrAdmin = !!selected && isActiveStatus && (selected.senderUid === uid || isTenantAdminUser);

  // (a) Not-Yet recovery: NEEDS_INFO + recoveryStage READY_FOR_INTAKE_REVIEW.
  const canMarkDoneRecovery = isSenderOrAdmin
    && selectedWorkflow?.status === 'NEEDS_INFO'
    && selectedWorkflow?.recoveryStage === 'READY_FOR_INTAKE_REVIEW'
    && selectedWorkflow?.sourceOutcomeStatus === 'not_yet';

  // (b) Consult-note request: CONSULT_NOTE_NEEDED ping + consult document present.
  const canMarkDoneConsultNote = isSenderOrAdmin
    && selected?.purpose === 'CONSULT_NOTE_NEEDED'
    && selectedWorkflow?.closedLoop?.hasConsultDocument === true;

  const canMarkDone = canMarkDoneRecovery || canMarkDoneConsultNote;

  // Confirmation-based purposes have no system-verifiable completion signal, so the
  // sender (or tenant_admin) resolves them manually once the action is confirmed.
  // Evidence-gated purposes keep their gated Mark Done above (no manual override).
  const EVIDENCE_GATED_PURPOSES = ['MISSING_INFO', 'MISSING_ATTACHMENT', 'CONSULT_NOTE_NEEDED'];
  const canSenderResolveManually = isSenderOrAdmin
    && !canMarkDone
    && !EVIDENCE_GATED_PURPOSES.includes(selected?.purpose);

  // A RETURN_TO_INTAKE ping is system-proven once its thread carries the
  // RETURN_TO_INTAKE_RESOLVED system event — the resolve button then reads
  // "Mark Complete" (proof) instead of "Resolve Ping" (manual). Same action.
  const hasSystemEvent = (eventType) =>
    messages.some((m) => m.messageType === MESSAGE_TYPE.SYSTEM_EVENT && m.eventType === eventType);
  const resolveLabel =
    (selected?.purpose === 'RETURN_TO_INTAKE' && hasSystemEvent('RETURN_TO_INTAKE_RESOLVED'))
      ? 'Mark Complete'
      : 'Resolve Ping';

  // Resolve a ping → RESOLVED (reuses resolveCommunicationPing). No workflow change,
  // no workflowEvents, no documents/PHI. Shared by gated Mark Done + manual Resolve.
  const doResolve = async (toastMsg, failMsg) => {
    if (!selected || actionBusy) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await resolveCommunicationPing(selected.tenantId, selected.referralId, selected.id, uid);
      showToast(toastMsg);
      // The ping is now RESOLVED — surface it in Resolved; selected stays in sync.
      setTab('resolved');
    } catch (err) {
      console.error('[ReferralPingsView] resolve failed:', err);
      setActionError(failMsg);
    } finally {
      setActionBusy(false);
    }
  };
  const markDone   = () => doResolve('Ping marked done.', 'Could not mark ping done.');
  const resolvePing = () => doResolve('Ping resolved.', 'Could not resolve ping.');

  const sendReply = async () => {
    if (!selected) return;
    const body = replyBody.trim();
    if (!body || replySending) return;
    setReplySending(true);
    setReplyError(null);
    try {
      await postCommunicationReply(selected.tenantId, selected.referralId, selected.id, {
        authorUid: uid,
        authorRole: user?.role,
        body,
        parentStatus: selected.status,
        parentRecipientUid: selected.recipientUid,
      });
      // Clear composer; the live thread listener renders the new message (no manual
      // append → no double render). Auto-ack status arrives via the live ping list.
      setReplyBody('');
      showToast('Reply sent.');
    } catch (err) {
      setReplyError(safeUserError(err));
    } finally {
      setReplySending(false);
    }
  };

  // Live listeners keep data current; Refresh is a fallback that re-subscribes.
  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
  };

  // Build the list for the active tab.
  // Active tabs (Waiting on Me / Sent by Me) show only OPEN/ACKNOWLEDGED pings;
  // RESOLVED and CANCELLED move to their own tabs.
  const isActivePing = (p) => p.status === COMMUNICATION_STATUS.OPEN || p.status === COMMUNICATION_STATUS.ACKNOWLEDGED;
  const list = useMemo(() => {
    if (tab === 'waiting') {
      return received.filter(isActivePing);
    }
    if (tab === 'sent') {
      return sent.filter(isActivePing);
    }
    // resolved / cancelled: union of received + sent, deduped by id, matching status.
    const wanted = tab === 'resolved' ? COMMUNICATION_STATUS.RESOLVED : COMMUNICATION_STATUS.CANCELLED;
    const byId = new Map();
    for (const p of [...received, ...sent]) {
      if (p.status === wanted) byId.set(p.id, p);
    }
    return [...byId.values()].sort((a, b) => fmtMs(b.createdAt) - fmtMs(a.createdAt));
  }, [tab, received, sent]);

  // Keep the selected ping in sync with refreshed data (or clear if gone).
  useEffect(() => {
    if (!selected) return;
    const fresh = [...received, ...sent].find((p) => p.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [received, sent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-tab unread indicators. Active tabs (Waiting / Sent) show counts; terminal
  // tabs (Resolved / Cancelled) show a dot only when an unread update exists there.
  const tabUnread = useMemo(() => {
    const active = (p) => p.status === COMMUNICATION_STATUS.OPEN || p.status === COMMUNICATION_STATUS.ACKNOWLEDGED;
    const waiting = received.filter((p) => active(p) && isPingUnread(p, uid)).length;
    const sentCount = sent.filter((p) => active(p) && isPingUnread(p, uid)).length;
    const byStatus = (wanted) => {
      const seen = new Set();
      for (const p of [...received, ...sent]) {
        if (p.status === wanted && isPingUnread(p, uid)) seen.add(p.id);
      }
      return seen.size > 0;
    };
    return {
      waiting,
      sent: sentCount,
      resolved: byStatus(COMMUNICATION_STATUS.RESOLVED),
      cancelled: byStatus(COMMUNICATION_STATUS.CANCELLED),
    };
  }, [received, sent, uid]);

  const isRecipient = selected && selected.recipientUid === uid;
  const isSender    = selected && selected.senderUid === uid;

  // Mark the selected thread read for the current participant when it is open and
  // unread for them — covers both initial open and live arrivals (the ping-list
  // listener updates `selected`, re-running this). Debounced to avoid write spam.
  // tenant_admin (neither sender nor recipient) is read-only: no write.
  const markReadTimerRef = useRef(null);
  const selUnread = selected ? isPingUnread(selected, uid) : false;
  useEffect(() => {
    if (!selected || !(isSender || isRecipient)) return;
    if (!selUnread) return;
    const side = isSender ? 'sender' : 'recipient';
    const { tenantId: t, referralId: r, id } = selected;
    if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    markReadTimerRef.current = setTimeout(() => {
      markPingRead(t, r, id, uid, side).catch((err) =>
        console.warn('[ReferralPingsView] markPingRead failed:', err?.message || err));
    }, 600);
    return () => { if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current); };
  }, [selectedId, selUnread, isSender, isRecipient]); // eslint-disable-line react-hooks/exhaustive-deps

  const canAck      = selected && isRecipient && selected.status === COMMUNICATION_STATUS.OPEN;
  const canCancel   = selected && isSender &&
    (selected.status === COMMUNICATION_STATUS.OPEN || selected.status === COMMUNICATION_STATUS.ACKNOWLEDGED);

  const runAction = async (fn) => {
    if (!selected) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await fn(selected.tenantId, selected.referralId, selected.id, uid);
      // Live ping list listener reflects the status change; no manual reload needed.
    } catch (err) {
      setActionError(safeUserError(err));
    } finally {
      setActionBusy(false);
    }
  };

  // Resolve the correct work surface for Open Referral: role default, with
  // purpose-aware overrides (a RETURN_TO_INTAKE ping is Needs-Info work, so it
  // routes Intake to the Needs Info / Not Yet Inbox surface, not the Workqueue).
  const getOpenReferralRoute = () => {
    const role = user?.role;
    if (role === 'intake_coordinator' && selected?.purpose === 'RETURN_TO_INTAKE') {
      return 'not-yet-inbox';
    }
    // GP: a Review Closed-Loop Summary ping is summary-review work (not a Not-Yet
    // recovery task), so it opens the referral inside My Submissions. All other GP
    // pings (MISSING_INFO / MISSING_ATTACHMENT / Not-Yet recovery) stay on the
    // Not Yet Inbox.
    if (role === 'gp_employee' && selected?.purpose === 'CLOSED_LOOP_AVAILABLE') {
      return 'my-submissions';
    }
    return OPEN_REFERRAL_ROUTE[role] || 'not-yet-inbox';
  };

  const openReferral = () => {
    if (!selected || !onNavigate) return;
    if (!selected.referralId) { setActionError('This ping is missing a referral link.'); return; }
    onNavigate(getOpenReferralRoute(), { referralId: selected.referralId, autoOpenReferralId: selected.referralId });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Live feedback toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl text-sm text-slate-100">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          {toast}
        </div>
      )}

      {/* Header */}
      <div className={`flex items-center justify-between gap-4 rounded-2xl ${hlSubtle('referral_pings_workspace')}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            <Bell className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Referral Pings</h1>
            <p className="text-xs text-slate-400">Referral-bound action threads for requests, replies, and system updates. No PHI, no documents.</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 text-slate-400 hover:text-white text-xs font-medium disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Type filter — Referral Pings vs Queue Pings */}
      <div className={`flex items-center gap-1 p-1 rounded-xl bg-slate-800/60 w-fit ${hl('referral_pings_type_filter')}`}>
        {[
          { id: 'all', label: 'All' },
          { id: 'referral', label: 'Referral Pings' },
          { id: 'queue', label: 'Queue Pings' },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setTypeFilter(id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              typeFilter === id ? 'bg-primary-soft text-brand' : 'text-slate-400 hover:text-white hover:bg-slate-700/60'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Queue Pings section */}
      {typeFilter !== 'referral' && (
        <div className={`space-y-3 rounded-2xl ${hl('referral_pings_queue_section')}`}>
          {typeFilter === 'all' && (
            <p className="text-[11px] uppercase tracking-wide text-amber-400/80 font-semibold">Queue Pings</p>
          )}
          <QueuePingsPanel onNavigate={onNavigate} />
        </div>
      )}

      {/* Referral Pings section */}
      {typeFilter === 'queue' ? null : (
      <>
      {typeFilter === 'all' && (
        <p className="text-[11px] uppercase tracking-wide text-violet-400/80 font-semibold">Referral Pings</p>
      )}
      {/* Tabs */}
      <div className={`flex items-center gap-1 p-1 rounded-xl bg-slate-800/60 w-fit ${hl('referral_pings_referral_tabs')}`}>
        {TABS.map(({ id, label, icon: Icon }) => {
          const count = id === 'waiting' ? tabUnread.waiting : id === 'sent' ? tabUnread.sent : 0;
          const dot   = (id === 'resolved' && tabUnread.resolved) || (id === 'cancelled' && tabUnread.cancelled);
          return (
            <button
              key={id}
              onClick={() => { setTab(id); setSelected(null); }}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                tab === id ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/60'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${tab === id ? 'bg-white/25 text-white' : 'bg-violet-500/30 text-violet-200'}`}>
                  {count > 99 ? '99+' : count}
                </span>
              )}
              {dot && count === 0 && (
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={handleRefresh} className="text-xs underline underline-offset-2 hover:text-red-300">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading pings…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* List */}
          <div className="lg:col-span-2 space-y-2">
            {list.length === 0 ? (
              <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-8 text-center text-sm text-slate-500">
                No pings in this view.
              </div>
            ) : list.map((p) => {
              const unread = isPingUnread(p, uid);
              return (
              <button
                key={p.id}
                onClick={() => { setSelected(p); setActionError(null); }}
                className={`w-full text-left rounded-2xl border p-4 transition-all ${
                  selected?.id === p.id
                    ? 'bg-slate-800/70 border-violet-500/40'
                    : unread
                      ? 'bg-slate-900/40 border-violet-500/30 hover:border-violet-500/50'
                      : 'bg-slate-900/40 border-slate-700/40 hover:border-slate-600/60'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    {unread && <span className="w-2 h-2 rounded-full bg-violet-400 flex-shrink-0" />}
                    <span className={`text-sm break-normal min-w-0 ${unread ? 'font-bold text-white' : 'font-medium text-white'}`}>{COMMUNICATION_PURPOSE_LABELS[p.purpose] || p.purpose}</span>
                  </span>
                  <StatusBadge status={p.status} />
                </div>
                <p className="text-xs text-slate-400 leading-snug">{p.actionNeeded}</p>
                <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                  <span className="truncate">{resolveName(p.senderUid, p.senderRole)} → {resolveName(p.recipientUid, p.recipientRole)}</span>
                  <span className="text-slate-700">·</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtWhen(p.createdAt)}</span>
                </div>
                <p className="text-[10px] text-slate-600 font-mono mt-1 truncate">{p.referralId}</p>
              </button>
              );
            })}
          </div>

          {/* Detail — Ping Thread */}
          <div className="lg:col-span-3" ref={detailRef}>
            {!selected ? (
              <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-10 text-center text-sm text-slate-500">
                Select a ping to view its thread.
              </div>
            ) : (
              <PingThreadDetail
                selected={selected}
                resolveName={resolveName}
                canAck={canAck}
                canCancel={canCancel}
                actionBusy={actionBusy}
                actionError={actionError}
                onOpenReferral={openReferral}
                onAck={() => runAction(acknowledgeCommunicationPing)}
                onCancel={() => runAction(cancelCommunicationPing)}
                canMarkDone={canMarkDone}
                onMarkDone={markDone}
                canResolve={canSenderResolveManually}
                onResolve={resolvePing}
                resolveLabel={resolveLabel}
                currentUid={uid}
                messages={messages}
                newMsgIds={newMsgIds}
                messagesLoading={messagesLoading}
                messagesError={messagesError}
                onRetryMessages={() => setMsgRefreshKey((k) => k + 1)}
                canReply={canReply}
                replyBody={replyBody}
                setReplyBody={setReplyBody}
                replySending={replySending}
                replyError={replyError}
                onSendReply={sendReply}
                activeFocusTarget={activeFocusTarget}
              />
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function fmtMs(ts) {
  if (!ts) return 0;
  return ts.toMillis ? ts.toMillis() : (ts.seconds ? ts.seconds * 1000 : (ts instanceof Date ? ts.getTime() : Date.parse(ts) || 0));
}
